import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRowClassNameParams, gridClasses } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import RawDataGrid from './DataGrid';
import { getJobTypeGroupLabel } from '@bloomays-lib/utils.shared';
import { JobType } from '@bloomays-lib/types.shared';
import { TamPrimeLine } from '../../types/monthlyPrime';
import { formatPrimeEuro, formatPrimeRate } from '../../utils/monthlyPrime.utils';

export type MonthlyPrimeTableProps = {
  rows: TamPrimeLine[];
  loading: boolean;
};

const MonthlyPrimeTable = ({ rows, loading }: MonthlyPrimeTableProps) => {
  const { t } = useTranslation(['primes']);

  const columns: GridColDef<TamPrimeLine>[] = useMemo(
    () => [
      {
        field: 'clientSocietyName',
        headerName: t('columnSociety'),
        flex: 1,
        minWidth: 120,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
      },
      {
        field: 'bloomerFullName',
        headerName: t('columnBloomer'),
        flex: 1,
        minWidth: 140,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
      },
      {
        field: 'jobType',
        headerName: t('columnContractType'),
        width: 120,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: JobType) => getJobTypeGroupLabel(value),
      },
      {
        field: 'monthlyMargin',
        headerName: t('columnMargin'),
        type: 'number',
        width: 110,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeEuro(value),
      },
      {
        field: 'monthlyPrime',
        headerName: t('columnCommission'),
        type: 'number',
        width: 120,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeEuro(value),
      },
      {
        field: 'splitRate',
        headerName: t('columnTamShare'),
        type: 'number',
        width: 110,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeRate(value),
      },
      {
        field: 'commissionRate',
        headerName: t('columnCommissionRate'),
        type: 'number',
        width: 130,
        headerClassName: 'header',
        cellClassName: 'cellStyle',
        valueFormatter: (value: number) => formatPrimeRate(value),
      },
    ],
    [t],
  );

  return (
    <TableSection>
      {!loading && rows.length === 0 ? <EmptyMessage variant="body2">{t('emptyRows')}</EmptyMessage> : null}
      <DataGrid
        uniqueDatagridId="monthly-prime-lines"
        fetchLoading={loading}
        getRowClassName={(params: GridRowClassNameParams) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        hideFooterPagination={rows.length <= 25}
        sortingMode="client"
        filterMode="client"
        rows={rows}
        getRowId={(row: TamPrimeLine) => row.jobFunnelProjectId}
        columns={columns}
        pageSize={25}
        pagination={rows.length > 25}
        sx={{ width: '100%' }}
      />
    </TableSection>
  );
};

export default MonthlyPrimeTable;

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));

const TableSection = styled('div')(() => ({
  marginTop: '24px',
  width: '100%',
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
}));
